import { onlineManager } from '@tanstack/react-query';
import { StorageEnum } from '../../modules/context/storage';
import { ExportOrderForm } from '../../modules/orders/export/V2/ExportOrderModal.types';
import {
  ProductDetailsFilterStateOnStorage,
  VerifyProductDetailsFilterStateOnStorage,
} from '../../modules/products/products-details-v2/types/productDetails.types';
import { GetOfflineEncryptionKeyOutput } from '../../services/modules/offline/offline.types';
import { IStorageItems } from '../../stores/OrderBook/orderBook.types';
import { SetCustomCatalogSnapshotParams } from './useStorage.interfaces';

const ELENTARI_STORAGE_KEY = 'user';
const getKeyFor = (itemName: string, teamId: string) => {
  const keyPlane = `tc_${itemName}_to_${teamId}`;
  const based = btoa(keyPlane);
  return based;
};

const getLocalStorageUser = () => {
  const user = localStorage.user ? JSON.parse(localStorage.user) : {};
  return user;
};

const setItem = (key: string, value: any) => {
  const user = getLocalStorageUser();
  const newUser = JSON.stringify({ ...user, [key]: value });
  return localStorage.setItem(ELENTARI_STORAGE_KEY, newUser);
};

const getItem = <T>(key: string): T => {
  const user = getLocalStorageUser();
  return user[key];
};

const removeItem = (key: string) => {
  const user = getLocalStorageUser();
  delete user[key];
  return localStorage.setItem(ELENTARI_STORAGE_KEY, JSON.stringify(user));
};

const hasItemBy = (key: string, teamId: string) => {
  const user = getLocalStorageUser();
  const searchKey = getKeyFor(key, teamId);
  return Object.prototype.hasOwnProperty.call(user, searchKey);
};

const makeGet =
  <T>(key: string, teamId: string) =>
  () => {
    const hashKey = getKeyFor(key, teamId);
    return getItem<T>(hashKey);
  };

const makeSet = (key: string, teamId: string) => (value: any) => {
  const hashKey = getKeyFor(key, teamId);
  setItem(hashKey, value);
};

const makeRemove = (key: string, teamId: string) => () => {
  const hashKey = getKeyFor(key, teamId);
  removeItem(hashKey);
};

const setProductDetailsFilterValue = (
  value: ProductDetailsFilterStateOnStorage,
  teamId: string,
) => {
  const filterState = makeGet<ProductDetailsFilterStateOnStorage | undefined>(
    StorageEnum.PRODUCT_DETAILS_FILTER,
    teamId,
  )();
  const newFilterState = {
    ...filterState,
    ...value,
  };
  return newFilterState;
};

const handleNetworkMode = (storageEnum: StorageEnum, isOfflineMode?: boolean) => {
  if (onlineManager.isOnline() && !isOfflineMode) {
    return storageEnum;
  }

  return `offline_${storageEnum}`;
};

const storage = {
  setItem,
  getItem,
  removeItem,
  hasItemBy,
  handleNetworkMode,
  getUser: getLocalStorageUser,
  getAdminTeamId: () => getItem(StorageEnum.ADMIN_TEAM_ID),
  getTeamId: () => getItem<string>(StorageEnum.TEAM_ID),
  getBrandId: () => getItem<string>(StorageEnum.BRAND_ID),
  getSessionId: () => getItem<string>(StorageEnum.SESSION_ID),
  setOrderBookStore: (orderBookStore: IStorageItems, teamId: string, isOfflineMode?: boolean) =>
    makeSet(handleNetworkMode(StorageEnum.ORDER_BOOK, isOfflineMode), teamId)(orderBookStore),
  getOrderBookStore: (teamId: string, isOfflineMode?: boolean) =>
    makeGet(handleNetworkMode(StorageEnum.ORDER_BOOK, isOfflineMode), teamId)(),
  removeOrderBookStore: (teamId: string, isOfflineMode?: boolean) =>
    makeRemove(handleNetworkMode(StorageEnum.ORDER_BOOK, isOfflineMode), teamId)(),
  setSelectedOrder: (value: string, teamId: string) =>
    makeSet(StorageEnum.SELECTED_ORDER, teamId)(value),
  getSelectedOrder: (teamId: string) => makeGet(StorageEnum.SELECTED_ORDER, teamId)(),
  removeSelectedOrder: (teamId: string) => makeRemove(StorageEnum.SELECTED_ORDER, teamId)(),
  setCustomCatalogSnapshot: <T>({
    snapshots,
    teamId,
    isOfflineMode,
  }: SetCustomCatalogSnapshotParams<T>) =>
    makeSet(
      handleNetworkMode(StorageEnum.CUSTOM_CATALOG_SNAPSHOT, isOfflineMode),
      teamId,
    )(snapshots),
  getCustomCatalogSnapshot: (teamId: string, isOfflineMode?: boolean) =>
    makeGet<unknown>(
      handleNetworkMode(StorageEnum.CUSTOM_CATALOG_SNAPSHOT, isOfflineMode),
      teamId,
    )(),
  getCameraId: (teamId: string) => makeGet<string>(StorageEnum.CAMERA_ID, teamId),
  setCameraId: (cameraId: string, teamId: string) =>
    makeSet(StorageEnum.CAMERA_ID, teamId)(cameraId),
  getProductDetailsFilter: (teamId: string): VerifyProductDetailsFilterStateOnStorage => {
    const filterState = makeGet<ProductDetailsFilterStateOnStorage>(
      StorageEnum.PRODUCT_DETAILS_FILTER,
      teamId,
    )();

    return {
      applyAllColors: filterState?.isAllColorsFilterSelected,
      applyAllOrders: filterState?.isAllOrdersFilterSelected,
      applyRelatedProducts: filterState?.isRelatedProductsFilterSelected,
      availabilityFilter: filterState?.availabilityFilter,
    };
  },
  setProductDetailsFilter: (teamId: string, value: ProductDetailsFilterStateOnStorage) => {
    makeSet(
      StorageEnum.PRODUCT_DETAILS_FILTER,
      teamId,
    )(setProductDetailsFilterValue(value, teamId));
  },
  setForceOffline: (value: boolean) => {
    localStorage.setItem(StorageEnum.FORCE_OFFLINE, value.toString());
  },
  getForceOffline: () => {
    return localStorage.getItem(StorageEnum.FORCE_OFFLINE) === 'true';
  },
  setLastLoginWasFromBrand: (value: boolean) => {
    localStorage.setItem(StorageEnum.LAST_LOGIN_WAS_FROM_A_BRAND, value.toString());
  },
  getLastLoginWasFromBrand: () => {
    return localStorage.getItem(StorageEnum.LAST_LOGIN_WAS_FROM_A_BRAND) === 'true';
  },
  setOfflineEncryptionKey: (teamId: string, value: GetOfflineEncryptionKeyOutput) =>
    makeSet(StorageEnum.OFFLINE_PRODUCTS_ENCRYPTION_KEY, teamId)(value),
  removeOfflineEncryptionKey: (teamId: string) =>
    makeRemove(StorageEnum.OFFLINE_PRODUCTS_ENCRYPTION_KEY, teamId)(),
  getOfflineEncryptionKey: (teamId: string) => {
    const valueFromStorage = makeGet<Record<number, number>>(
      StorageEnum.OFFLINE_PRODUCTS_ENCRYPTION_KEY,
      teamId,
    )();

    if (!valueFromStorage) {
      return valueFromStorage;
    }

    return Uint8Array.from(Object.values(valueFromStorage));
  },
  setExportOrderConfig: (teamId: string, value: ExportOrderForm) =>
    makeSet(StorageEnum.EXPORT_ORDER_MODAL_CONFIG, teamId)(value),

  getExportOrderConfig: (teamId: string) =>
    makeGet<ExportOrderForm>(StorageEnum.EXPORT_ORDER_MODAL_CONFIG, teamId)(),

  removeExportOrderConfig: (teamId: string) =>
    makeRemove(StorageEnum.EXPORT_ORDER_MODAL_CONFIG, teamId)(),

  setGlobalSearchRecents: (teamId: string, value: string[]) =>
    makeSet(StorageEnum.GLOBAL_SEARCH_RECENTS, teamId)(value),

  getGlobalSearchRecents: (teamId: string) =>
    makeGet<string[]>(StorageEnum.GLOBAL_SEARCH_RECENTS, teamId)(),

  removeGlobalSearchRecents: (teamId: string) =>
    makeRemove(StorageEnum.GLOBAL_SEARCH_RECENTS, teamId)(),

  setDismissedAnnouncementId: (announcementId: string, teamId: string) =>
    localStorage.setItem(getKeyFor(StorageEnum.DISMISSED_ANNOUNCEMENT, teamId), announcementId),

  getDismissedAnnouncementId: (teamId: string) =>
    localStorage.getItem(getKeyFor(StorageEnum.DISMISSED_ANNOUNCEMENT, teamId)) ?? undefined,

  setReadHighlightIds: (highlightIds: string[], userId: string) =>
    localStorage.setItem(
      getKeyFor(StorageEnum.READ_HIGHLIGHTS, userId),
      JSON.stringify(highlightIds),
    ),

  getReadHighlightIds: (userId: string) => {
    const stored = localStorage.getItem(getKeyFor(StorageEnum.READ_HIGHLIGHTS, userId));

    if (!stored) {
      return undefined;
    }

    try {
      const parsed: unknown = JSON.parse(stored);

      if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === 'string')) {
        return undefined;
      }

      return parsed;
    } catch {
      return undefined;
    }
  },
};

export default storage;
